import { useState } from 'react';
import { useQueries } from 'react-query';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  getAsmVulnerabilityPaged,
  getSeverityList,
  getVulnerabilityCounter,
  getVulnerabilityList,
} from 'services/asm-service';
import { useAuthTenant } from 'store/auth';
import {
  BlockTitle,
  CardCounter,
  ContainerLogo,
  ContentCardCounter,
  InfoVulBlock,
  InfoVulFullBlock,
  TitleCardCounter,
} from '../styles';
import { Bar, BarChart, Legend, ResponsiveContainer, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from 'recharts';
import { DefaultTable } from 'Components/DefaultTable';
import { Pagination, Stack } from '@mui/material';
import { Shimmer } from 'Components/PageLoader/styles';
import { PageLoaderList } from 'Components/PageLoader/List';
import { tableHeaderAsmVul } from './table-header-asm-vulnerability';

export const TabVulnerability = () => {
  const { t } = useTranslation(['asm']);
  const [page, setPage] = useState(1);
  const selectedTenant = useSelector(useAuthTenant);

  const queries = useQueries([
    {
      queryKey: ['getVulnerabilityCounter'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;

        const response = await getVulnerabilityCounter(selectedTenant.slug);

        if (response?.data && response?.data?.vulnerabilityCounter) {
          return response?.data?.vulnerabilityCounter;
        }
        return 0;
      },
    },
    {
      queryKey: ['getSeverityList'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;

        const response = await getSeverityList(selectedTenant.slug);

        if (response?.data && response?.data?.severities) {
          return response?.data?.severities;
        }
        return [];
      },
    },
    {
      queryKey: ['getVulnerabilityList'],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return null;

        const response = await getVulnerabilityList(selectedTenant.slug);

        if (response?.data && response?.data?.vulnerabilities) {
          return response?.data?.vulnerabilities;
        }
        return [];
      },
    },
    {
      queryKey: ['getAsmVulnerabilityPaged', page],
      queryFn: async () => {
        if (selectedTenant.slug === undefined) return [];
        const result = await getAsmVulnerabilityPaged(selectedTenant.slug, page, 6);

        if (result?.data?.data.length > 0) {
          return result?.data;
        }
        return {
          data: [],
          page,
          totalPages: 0,
        };
      },
    },
  ]);

  const [
    {
      data: vulnerabilityCounter,
      isLoading: vulnerabilityCounterLoading,
      // isError: vulnerabilityCounterError
    },
    {
      data: severities,
      isLoading: severityLoading,
      // isError: severityError
    },
    {
      data: vulnerabilities,
      isLoading: vulnerabilityLoading,
      // isError: vulnerabilityError
    },
    {
      data: pagedAsmVulnerability,
      isLoading: asmVulnerabilityLoading,
      // isError: asmVulnerabilityError
    },
  ] = queries;

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const { name, qt } = payload[0].payload;
      return (
        <div style={{ backgroundColor: '#ffffff', padding: '5px', border: '1px solid #ccc' }}>
          <p>{`${name}: ${qt}`}</p>
        </div>
      );
    }
    return null;
  };

  const COLORS = [
    '#0088FE',
    '#00C49F',
    '#FFBB28',
    '#FF8042',
    '#AF19FF',
    '#19FF92',
    '#FF1942',
    '#1994FF',
    '#FFDF19',
    '#C219FF',
  ];

  return (
    <>
      {/* Container para o card contador e gráfico de pizza lado a lado */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'row', 
        gap: '24px', 
        marginBottom: '40px',
        flexWrap: 'wrap',
        alignItems: 'stretch',
        width: '100%',
        maxWidth: '100%',
        padding: '0'
      }} data-tour="vuln-overview">
        <CardCounter
          style={{
            minWidth: '220px',
            flexShrink: 0,
            height: '300px',
            background: 'linear-gradient(180deg, #F9FBFF 0%, #FFFFFF 100%)',
            border: '1px solid #EEF2FF',
            boxShadow: '0px 8px 24px #EEF2FF',
            padding: '20px',
          }}
        >
          {!vulnerabilityCounterLoading && (
            <>
              <TitleCardCounter style={{ color: 'var(--color-primary, #0039c5)', letterSpacing: '0.3px', marginBottom: '8px' }}>
                {t('asm:tabVulnerability.cardTitle')}
              </TitleCardCounter>
              <ContentCardCounter style={{ fontSize: '44px', gap: '12px', alignItems: 'center' }}>
                {vulnerabilityCounter}
                <ContainerLogo style={{ width: '40px', height: '40px' }}>
                  <img src="img/icons/globe.png" alt="logo de um globo" />
                </ContainerLogo>
              </ContentCardCounter>
            </>
          )}
          {vulnerabilityCounterLoading && (
            <Shimmer></Shimmer>
          )}
        </CardCounter>
        
        <InfoVulBlock style={{ flex: '1', minWidth: '400px', height: '300px' }} data-tour="severity-chart">
           {!severityLoading && (
             <>
               <BlockTitle>{t('asm:tabVulnerability.chartSeverities')}</BlockTitle>
               <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={400}>
                 <PieChart
                   width={450}
                   height={300}
                  margin={{
                    top: 0,
                    right: 16,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <Pie
                    cx="50%"
                    cy="50%"
                    data={severities}
                    dataKey={'qt'}
                    isAnimationActive={true}
                    nameKey={'name'}
                    innerRadius={50}
                  >
                    {severities.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend verticalAlign="top" />
                </PieChart>
              </ResponsiveContainer>
            </>
          )}
          {severityLoading && <Shimmer style={{ height: '200px', width: '100%' }}></Shimmer>}
        </InfoVulBlock>
      </div>
      
      {/* Gráfico de vulnerabilidades em largura total */}
      <InfoVulFullBlock style={{ marginBottom: '40px' }} data-tour="vuln-chart">
        {!vulnerabilityLoading && (
          <>
            <BlockTitle>{t('asm:tabVulnerability.chartLabel')}</BlockTitle>
            <ResponsiveContainer width="100%" height="100%" minHeight={200} minWidth={700}>
              <BarChart
                layout={'vertical'}
                width={700}
                height={300}
                data={vulnerabilities}
                margin={{
                  top: 0,
                  right: 16,
                  left: 24,
                  bottom: 0,
                }}
                maxBarSize={vulnerabilities.reduce((max, obj) => (obj.qt > max.qt ? obj : max), [0])[0].qt}
              >
                <XAxis type="number" domain={[0, 'dataMax + 1']} axisLine={false} />
                <YAxis dataKey={'name'} type="category" width={160} tickMargin={8} />
                <Tooltip content={<CustomTooltip />} />
                <Legend verticalAlign="top" />
                <Bar name={t('asm:tabVulnerability.chartLabel')} dataKey="qt" barSize={24} fill="rgb(134,239,172)" />
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
        {vulnerabilityLoading && <Shimmer style={{ height: '200px', width: '100%' }}></Shimmer>}
      </InfoVulFullBlock>
      {!asmVulnerabilityLoading && (
        <>
          <div data-tour="vuln-table">
          <DefaultTable
            rows={tableHeaderAsmVul(t)}
            cells={pagedAsmVulnerability?.data || []}
            showNoDataLine
            applyMB={pagedAsmVulnerability?.totalPages < 1}
          />
          </div>
          {pagedAsmVulnerability?.totalPages > 0 && (
            <Stack sx={{ justifyContent: 'center', flexDirection: 'row', marginBlock: '16px', marginInline: 'auto' }} data-tour="vuln-pagination">
              <Pagination
                count={pagedAsmVulnerability?.totalPages || 0}
                onChange={(_, selectedPage) => {
                  setPage(selectedPage);
                }}
                color="primary"
                page={page}
                showFirstButton
                showLastButton
              />
            </Stack>
          )}
        </>
      )}
      {asmVulnerabilityLoading && <PageLoaderList />}
    </>
  );
};

